import type { ScopedThreshold } from "./monitor-config";
import { runOnWorker } from "../../services/worker-run-client";
import type { LoadedPlan } from "../../checkpoint-vm/plans";
import { loadCheckpointVm } from "../../checkpoint-vm/checkpoint-vm";
import { adminRpc } from "../../services/admin-rpc-client";
import { parseTypeTree, type TypeTree } from "./type-tree";
import type { SignalKeySpec } from "../../pages/monitor/MonitorSignalChips";

// What a form for a checkpoint monitor plugin needs: the plugin as its fileset registers it (the function
// filling each member), the messages its validate answers mapped to the fields they name, and the monitor's
// extra_data. Generic over plugins; the nats form is the first user.

// One member of a plugin declaration as fileset.extra_data.pluginDecls registers it.
interface RegisteredMember {
  name: string;
  module?: string;
  function?: string;
  literalJson?: string;
}

interface RegisteredDecl {
  name: string;
  module: string;
  contractType?: string;
  members: RegisteredMember[];
  config?: unknown;
}

// RegisteredPlugin is a plugin of a fileset, as a host calls it.
export interface RegisteredPlugin {
  filesetUid: string;
  revisionUid: string;
  name: string;
  module: string; // the module declaring the plugin
  kind: string;
  /** the plugin's title literal, "" when it has none */
  title: string;
  /** the plugin's description literal: what it watches, in a line; "" when it has none */
  description: string;
  /** the generic record type the instance fills: "MonitorPlugin" */
  contractType: string;
  members: Record<string, { module: string; function: string }>;
  /** the configuration type as a tree, which the form is built from; absent on a registration from before the tree */
  config?: TypeTree;
  /** the module's form module (`<module>.form.ts`), compiled at push: its JavaScript; absent when the module has none */
  form?: CompiledFormModule;
  /** the readings the monitors list shows as chips, as the instance's `signals` literal declares them */
  signals: SignalKeySpec[];
}

/** The `signals` literal of a MonitorPlugin instance (core.monitor Signal: key, label, direction, yellow, red) as chip specs. */
export function signalsOf(literalJson: string | undefined): SignalKeySpec[] {
  const raw = literalJson ? parse(literalJson) : undefined;
  if (!Array.isArray(raw)) return [];
  const out: SignalKeySpec[] = [];
  for (const s of raw as Array<Record<string, unknown>>) {
    if (!s || typeof s.key !== "string" || s.key === "") continue;
    const direction = String(s.direction ?? "Ascending").toLowerCase();
    out.push({
      key: s.key,
      label: typeof s.label === "string" ? s.label : "",
      direction: direction === "bool" ? "bool" : direction === "descending" ? "descending" : "ascending",
      ...(typeof s.yellow === "number" ? { yellow: s.yellow } : {}),
      ...(typeof s.red === "number" ? { red: s.red } : {}),
    });
  }
  return out;
}

/** A form module as the push keeps it on the fileset (extra_data.forms[module]). */
export interface CompiledFormModule {
  js: string;
  typescriptVersion: string;
}

/** The fileset's compiled form modules by checkpoint module, from its extra_data. */
function formsOf(extra: unknown): Record<string, CompiledFormModule> {
  const forms = (parse(extra) as { forms?: unknown } | undefined)?.forms;
  const out: Record<string, CompiledFormModule> = {};
  if (!forms || typeof forms !== "object") return out;
  for (const [module, f] of Object.entries(forms as Record<string, unknown>)) {
    const m = f as { js?: unknown; typescriptVersion?: unknown } | undefined;
    if (typeof m?.js === "string" && m.js !== "") out[module] = { js: m.js, typescriptVersion: String(m.typescriptVersion ?? "") };
  }
  return out;
}

function parse(v: unknown): unknown {
  let cur = v;
  for (let i = 0; i < 3 && typeof cur === "string"; i++) {
    try {
      cur = JSON.parse(cur);
    } catch {
      break;
    }
  }
  return cur;
}

// pluginFromDecls reads plugin `name` out of a fileset's registration; undefined when it declares none.
export function pluginFromDecls(filesetUid: string, revisionUid: string, decls: unknown, name: string, forms: Record<string, CompiledFormModule> = {}): RegisteredPlugin | undefined {
  const all = parse(decls);
  if (!Array.isArray(all)) return undefined;
  const d = (all as RegisteredDecl[]).find((x) => x?.name === name);
  if (!d) return undefined;
  return registered(filesetUid, revisionUid, d, forms);
}

function registered(filesetUid: string, revisionUid: string, d: RegisteredDecl, forms: Record<string, CompiledFormModule>): RegisteredPlugin {
  const members: RegisteredPlugin["members"] = {};
  let kind = "";
  let title = "";
  let description = "";
  let signals: SignalKeySpec[] = [];
  for (const m of d.members ?? []) {
    if (m.function) members[m.name] = { module: m.module ?? d.module, function: m.function };
    else if (m.name === "kind" && m.literalJson) kind = String(parse(m.literalJson) ?? "");
    else if (m.name === "title" && m.literalJson) title = String(parse(m.literalJson) ?? "");
    else if (m.name === "description" && m.literalJson) description = String(parse(m.literalJson) ?? "");
    else if (m.name === "signals") signals = signalsOf(m.literalJson);
  }
  const config = parseTypeTree(d.config);
  const form = forms[d.module];
  return {
    filesetUid, revisionUid, name: d.name, module: d.module, kind, title, description, contractType: d.contractType ?? "", members, signals,
    ...(config ? { config } : {}),
    ...(form ? { form } : {}),
  };
}

// workersWithRunner is the uid of every worker whose job runner is up: the runner registers itself as
// the worker's "job-runner" job, and its run has no completedat while it lives (what a8 job-status
// reads too). A function run on any other worker waits a minute and answers nothing.
export async function workersWithRunner(): Promise<Set<string>> {
  const r = await adminRpc.query(
    `SELECT DISTINCT j.workeruid FROM job j JOIN processrun pr ON pr.job_uid = j.uid WHERE j.name = 'job-runner' AND pr.completedat IS NULL`,
  );
  return new Set((r.rows as Array<{ workeruid?: string }>).map((row) => String(row.workeruid ?? "")).filter((uid) => uid !== ""));
}

/** A worker as the form offers it: its login on its server. */
export interface KnownWorker {
  uid: string;
  login: string;
  server: string;
  serverUid: string;
}

// listWorkers is every active worker with its server. The accounts continuum knows on a server are the
// logins of that server's workers, so a root worker's execution user is a choice among the other
// logins on its server (sandboxAccountsFor), not a box to spell right.
export async function listWorkers(): Promise<KnownWorker[]> {
  const r = await adminRpc.query(
    `SELECT w.uid, w.login, s.uid AS serveruid, s.name AS server FROM worker w JOIN server s ON s.uid = w.serveruid WHERE w.status = 'active' ORDER BY s.name, w.login`,
  );
  return (r.rows as Array<{ uid?: string; login?: string; serveruid?: string; server?: string }>)
    .filter((row) => row.uid && row.login)
    .map((row) => ({ uid: String(row.uid), login: String(row.login), server: String(row.server ?? ""), serverUid: String(row.serveruid ?? "") }));
}

export const workerLabel = (w: KnownWorker) => `${w.login}@${w.server}`;

/** The non-root accounts known on a worker's server: the logins of that server's other workers. */
export function sandboxAccountsFor(workers: KnownWorker[], workerUid: string): string[] {
  const w = workers.find((x) => x.uid === workerUid);
  if (!w) return [];
  return [...new Set(workers.filter((x) => x.serverUid === w.serverUid && x.uid !== w.uid && x.login !== "root").map((x) => x.login))].sort();
}

/** The runner option's text: its label, and that its runner is down when it is (only a monitor's saved worker). */
export const NO_RUNNER = " (runner down)";
export const workerOptionText = (label: string, hasRunner: boolean) => (hasRunner ? label : label + NO_RUNNER);

// A monitor plugin as a fileset registers it, with the fileset it is in: what the Add Monitor picker lists.
export interface ListedMonitorPlugin extends RegisteredPlugin {
  fileset: string;
  /** the plugin collects its own readings; one that does not is captured by the platform or a command */
  collects: boolean;
}

// listMonitorPlugins is every monitor plugin an active fileset registers (an instance of MonitorPlugin)
// that collects: one with no collect member is captured by the platform (a job's runs, a daemon), and a
// monitor on it is made by enrolling the job, not by picking it here.
export async function listMonitorPlugins(): Promise<ListedMonitorPlugin[]> {
  const r = await adminRpc.query(`SELECT uid, name, current_revision, extra_data FROM fileset WHERE status = 'active' ORDER BY name`);
  const out: ListedMonitorPlugin[] = [];
  for (const row of r.rows as Array<{ uid?: string; name?: string; current_revision?: string; extra_data?: unknown }>) {
    if (!row.uid || !row.current_revision) continue;
    const extra = parse(row.extra_data) as { pluginDecls?: unknown } | undefined;
    const all = parse(extra?.pluginDecls);
    if (!Array.isArray(all)) continue;
    const forms = formsOf(extra);
    for (const d of all as RegisteredDecl[]) {
      if (d?.contractType !== "MonitorPlugin") continue;
      const plugin = registered(row.uid, row.current_revision, d, forms);
      out.push({ ...plugin, fileset: String(row.name ?? ""), collects: Boolean(plugin.members.collect) });
    }
  }
  return out.sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name));
}

// monitorPluginsOfFileset is every monitor plugin (an instance of MonitorPlugin) a fileset row registers,
// collecting or not: what a job's monitor may be judged by (runMonitor, daemonMonitor, a fileset's own).
export function monitorPluginsOfFileset(row: { uid?: string; current_revision?: string | null; extra_data?: unknown }): RegisteredPlugin[] {
  if (!row.uid || !row.current_revision) return [];
  const extra = parse(row.extra_data) as { pluginDecls?: unknown } | undefined;
  const all = parse(extra?.pluginDecls);
  if (!Array.isArray(all)) return [];
  const forms = formsOf(extra);
  return (all as RegisteredDecl[])
    .filter((d) => d?.contractType === "MonitorPlugin")
    .map((d) => registered(row.uid as string, row.current_revision as string, d, forms))
    .sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name));
}

// resolvePluginByUid finds plugin `name` of the fileset with that uid.
export async function resolvePluginByUid(filesetUid: string, name: string): Promise<RegisteredPlugin> {
  const r = await adminRpc.query(
    `SELECT uid, name, current_revision, extra_data FROM fileset WHERE uid = '${filesetUid.replace(/'/g, "''")}' AND status = 'active' LIMIT 1`,
  );
  const row = r.rows[0] as { uid?: string; name?: string; current_revision?: string; extra_data?: unknown } | undefined;
  if (!row?.uid) throw new Error(`There is no fileset ${filesetUid}.`);
  if (!row.current_revision) throw new Error(`Fileset ${row.name ?? filesetUid} has no pushed revision.`);
  const extra = parse(row.extra_data) as { pluginDecls?: unknown } | undefined;
  const plugin = pluginFromDecls(row.uid, row.current_revision, extra?.pluginDecls, name, formsOf(extra));
  if (!plugin) throw new Error(`Fileset ${row.name ?? filesetUid} declares no plugin ${name}: push it once so its plugins are registered.`);
  return plugin;
}

// runPluginFunction runs one function of the plugin on the worker and answers what it returned, parsed.
// The worker's job runner is asked directly and the leaf it launches answers this browser
// (worker-run-client); nothing about the run goes through a job.
export async function runPluginFunction(
  plugin: RegisteredPlugin,
  module: string,
  fn: string,
  input: unknown,
  worker: PluginWorker,
  reason: string,
): Promise<unknown> {
  const r = await runOnWorker(worker.uid, {
    start: {
      filesetUid: plugin.filesetUid,
      filesetRevisionUid: plugin.revisionUid,
      mainModule: module,
      function: fn,
      inputJson: input === undefined ? undefined : JSON.stringify(input),
    },
    executionUser: worker.executionUser.trim() || undefined,
    reason,
  });
  if (r.state !== "finished") throw new Error(r.message || `${fn} did not answer (${r.state})`);
  return r.resultJson ? JSON.parse(r.resultJson) : null;
}

// callPluginFunction runs one function of the plugin's plan in the tab's checkpoint vm (the plan the
// form already loaded) and answers what it returned: a form module's sdk.call. The vm refuses a
// function that needs what the browser does not offer; a function that pauses is refused too, since
// a form has nothing to resume it with.
export async function callPluginFunction(loaded: LoadedPlan, module: string, fn: string, input: unknown): Promise<unknown> {
  const out = await loaded.vm.run(loaded.handle, module, fn, input === undefined ? [] : [input]).result;
  if (out.kind === "paused") throw new Error(`${module}.${fn} paused at a yield, which a form cannot resume`);
  return out.value;
}

// validatePluginConfig runs the plugin's validate with the config on the worker; a plugin with no validate
// answers no messages.
export async function validatePluginConfig(plugin: RegisteredPlugin, config: unknown, worker: PluginWorker, reason: string): Promise<PluginMessage[]> {
  const validate = plugin.members["validate"];
  if (!validate) return [];
  const result = await runPluginFunction(plugin, validate.module, validate.function, config, worker, reason);
  // the answer is read by the Go reader every host uses (checkpoint instance.ReadVerdict), in the tab's vm
  return (await loadCheckpointVm()).verdict(result).messages;
}

// resolvePlugin finds plugin `name` of the fileset of that name.
export async function resolvePlugin(filesetName: string, name: string): Promise<RegisteredPlugin> {
  const r = await adminRpc.query(
    `SELECT uid, current_revision, extra_data FROM fileset WHERE name = '${filesetName.replace(/'/g, "''")}' AND status = 'active' LIMIT 1`,
  );
  const row = r.rows[0] as { uid?: string; current_revision?: string; extra_data?: unknown } | undefined;
  if (!row?.uid) throw new Error(`There is no fileset ${filesetName}.`);
  if (!row.current_revision) throw new Error(`Fileset ${filesetName} has no pushed revision.`);
  const extra = parse(row.extra_data) as { pluginDecls?: unknown } | undefined;
  const plugin = pluginFromDecls(row.uid, row.current_revision, extra?.pluginDecls, name, formsOf(extra));
  if (!plugin) throw new Error(`Fileset ${filesetName} declares no plugin ${name}: push it once so its plugins are registered.`);
  return plugin;
}

// A message validate answers.
export interface PluginMessage {
  level: string; // "Info" | "Warn" | "Error"
  text: string;
  path: string; // "seeds[0]", "cluster"; "" for the config as a whole
}

// messagesAt is the messages naming a field, by path. A message whose path names no field the form shows
// goes to "" (the top of the form), so it is never dropped.
export function messagesAt(messages: PluginMessage[], shown: string[]): Map<string, PluginMessage[]> {
  const out = new Map<string, PluginMessage[]>();
  for (const m of messages) {
    const at = shown.includes(m.path) ? m.path : "";
    out.set(at, [...(out.get(at) ?? []), m]);
  }
  return out;
}

export const hasLevel = (messages: PluginMessage[], level: string) => messages.some((m) => m.level === level);

// pluginColumns is the monitor row's naming of its plugin: plugin_fileset_uid + plugin_name
// (FEATURE-20261025-unify-the-checker-pattern: every monitor names the MonitorPlugin that judges it).
export function pluginColumns(plugin: { filesetUid: string; name: string }): { plugin_fileset_uid: string; plugin_name: string } {
  return { plugin_fileset_uid: plugin.filesetUid, plugin_name: plugin.name };
}

// pluginExtraData is a plugin monitor's extra_data: its config, keeping every other key the row had
// (thresholds set with `a8 monitor threshold`, say), and no plugin pointer (the row's columns name it).
export function pluginExtraData(existing: unknown, config: unknown, overrides?: ScopedThreshold[]): string {
  const prior = parse(existing);
  const base = prior && typeof prior === "object" && !Array.isArray(prior) ? (prior as Record<string, unknown>) : {};
  const doc: Record<string, unknown> = { ...base, config };
  delete doc["plugin"];
  if (overrides) {
    // A plugin monitor has no kind, so its thresholds are the operator's overrides alone, as godev
    // monitorconfig writes them (a8 monitor threshold set).
    delete doc["overrides"];
    if (overrides.length) doc["overrides"] = overrides;
    doc["thresholds"] = overrides;
  }
  return JSON.stringify(doc);
}

// The worker a plugin monitor collects from, and the account its jobs run as: a root runner runs a job
// only as a non-root account in a transient unit, as `a8 monitor enroll --execution-user` does.
export interface PluginWorker {
  uid: string;
  executionUser: string; // "" for a worker that runs its jobs as itself
}

export const isRootWorker = (workerLabel: string) => workerLabel.startsWith("root@");

// executionUserProblem is why this worker and execution user cannot run a monitor's jobs, "" when they can:
// the rules of the CLI's resolveRootWorker.
export function executionUserProblem(workerLabel: string, executionUser: string): string {
  const user = executionUser.trim();
  if (user === "root" || user === "0") return "an execution user of root is refused: give a non-root account";
  if (isRootWorker(workerLabel) && !user) {
    return `${workerLabel} is a root worker: give the non-root account its jobs run as (e.g. continuum-dev)`;
  }
  if (!isRootWorker(workerLabel) && user) return `only a root worker takes an execution user; ${workerLabel} runs its jobs as itself`;
  return "";
}

// The job of a plugin monitor: a checkpoint job of the plugin's fileset; JobTurn runs the plugin's collect. On
// a root worker it runs as the execution user in a transient unit.
export function pluginJobPayload(jobName: string, executionUser = ""): string {
  const payload: Record<string, unknown> = {
    jobName,
    command: ["a8", "checkpoint", "exec", "--job-event"],
    runDirect: true,
    captureConsole: false,
  };
  if (executionUser.trim()) {
    payload.backend = "systemd-transient";
    payload.executionUser = executionUser.trim();
  }
  return JSON.stringify(payload);
}

